import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2 } from 'lucide-react';

interface Company { id: string; name: string }

interface UserItem {
  id: string;
  email: string;
  name: string;
  role: 'GLOBAL_ADMIN' | 'COMPANY_MANAGER' | 'USER';
  companyId?: string | null;
  active: boolean;
  createdAt?: string;
  company?: { name: string } | null;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'USER' as UserItem['role'],
    companyId: '',
    active: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, companiesRes] = await Promise.all([
        api.get('/users'),
        api.get('/companies')
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
    } catch {
      toast.error('Falha ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (user?: UserItem) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        name: user.name,
        role: user.role,
        companyId: user.companyId || '',
        active: user.active
      });
    } else {
      setEditingUser(null);
      setFormData({ email: '', password: '', name: '', role: 'USER', companyId: '', active: true });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const payload: any = {
          name: formData.name,
          role: formData.role,
          companyId: formData.role === 'GLOBAL_ADMIN' ? null : formData.companyId || null,
          active: formData.active
        };
        if (formData.password) payload.password = formData.password;
        await api.put(`/users/${editingUser.id}`, payload);
        toast.success('Usuário atualizado');
      } else {
        await api.post('/users', {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: formData.role,
          companyId: formData.role === 'GLOBAL_ADMIN' ? null : formData.companyId || null
        });
        toast.success('Usuário criado');
      }
      setShowModal(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Operação falhou');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este usuário?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('Usuário excluído');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Falha ao excluir');
    }
  };

  const roleNeedsCompany = formData.role !== 'GLOBAL_ADMIN';

  const roleLabel = (role: string) => ({
    GLOBAL_ADMIN: 'Admin Global',
    COMPANY_MANAGER: 'Gerente',
    USER: 'Usuário'
  }[role] || role);

  if (loading) {
    return <div className="spinner-wrap"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-header__title">Usuários</h1>
        <button className="btn btn--primary" onClick={() => openModal()}>
          <Plus size={16} /> Novo Usuário
        </button>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Perfil</th>
              <th>Empresa</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td style={{ fontWeight: 500 }}>{user.name}</td>
                <td style={{ color: '#718096' }}>{user.email}</td>
                <td>
                  <span className="badge badge--blue">{roleLabel(user.role)}</span>
                </td>
                <td>{user.company?.name || '–'}</td>
                <td>
                  <span className={`badge ${user.active ? 'badge--green' : 'badge--red'}`}>
                    {user.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn--outline btn--sm" onClick={() => openModal(user)}>
                      <Edit size={13} /> Editar
                    </button>
                    <button className="btn btn--danger btn--sm" onClick={() => handleDelete(user.id)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: '#718096', padding: '32px 16px' }}>
                  Nenhum usuário encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal__header">
              <h2 className="modal__title">{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h2>
              <button className="btn btn--outline btn--sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal__body">
                <div className="form-group">
                  <label className="form-label">Nome</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                {!editingUser && (
                  <>
                    <div className="form-group">
                      <label className="form-label">E-mail</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Senha</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label className="form-label">Perfil</label>
                  <select
                    value={formData.role}
                    onChange={e => setFormData({
                      ...formData,
                      role: e.target.value as UserItem['role'],
                      companyId: e.target.value === 'GLOBAL_ADMIN' ? '' : formData.companyId
                    })}
                    className="form-input"
                  >
                    <option value="GLOBAL_ADMIN">Admin Global</option>
                    <option value="COMPANY_MANAGER">Gerente</option>
                    <option value="USER">Usuário</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Empresa</label>
                  <select
                    value={formData.companyId}
                    onChange={e => setFormData({ ...formData, companyId: e.target.value })}
                    className="form-input"
                    disabled={!roleNeedsCompany}
                    required={roleNeedsCompany}
                  >
                    <option value="">Selecione</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {editingUser && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select
                        value={formData.active ? 'active' : 'inactive'}
                        onChange={e => setFormData({ ...formData, active: e.target.value === 'active' })}
                        className="form-input"
                      >
                        <option value="active">Ativo</option>
                        <option value="inactive">Inativo</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nova Senha</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Deixe em branco para manter a atual"
                        className="form-input"
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="modal__footer">
                <button type="button" className="btn btn--outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn--primary">
                  {editingUser ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
